if (!customElements.get('tabs-component')) {
  class TabsComponent extends HTMLElement {
    constructor() {
      super();
      this.tabList = this.querySelector('[role="tablist"]');
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');
      this.collapsedInitial = this.dataset.collapsedInitial === 'true';
      this.hasInteracted = false;

      this.setupEventListeners();
      this.handleInitialHash();
    }

    setupEventListeners() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick.bind(this));
        tab.addEventListener('keydown', this.handleKeyDown.bind(this));
      });

      window.addEventListener('hashchange', this.handleHashChange.bind(this));
    }

    handleTabClick(event) {
      const clickedTab = event.currentTarget;
      const tabIndex = clickedTab.dataset.tabIndex;
      this.switchTab(clickedTab, tabIndex);
    }

    handleKeyDown(event) {
      const currentTab = event.currentTarget;
      let targetTab;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          targetTab = this.getPreviousTab(currentTab);
          break;
        case 'ArrowRight':
          event.preventDefault();
          targetTab = this.getNextTab(currentTab);
          break;
        case 'Home':
          event.preventDefault();
          targetTab = this.tabs[0];
          break;
        case 'End':
          event.preventDefault();
          targetTab = this.tabs[this.tabs.length - 1];
          break;
        default:
          return;
      }

      if (targetTab) {
        targetTab.focus();
        this.switchTab(targetTab, targetTab.dataset.tabIndex);
      }
    }

    getPreviousTab(currentTab) {
      const currentIndex = Array.from(this.tabs).indexOf(currentTab);
      return currentIndex > 0 ? this.tabs[currentIndex - 1] : this.tabs[this.tabs.length - 1];
    }

    getNextTab(currentTab) {
      const currentIndex = Array.from(this.tabs).indexOf(currentTab);
      return currentIndex < this.tabs.length - 1 ? this.tabs[currentIndex + 1] : this.tabs[0];
    }

    handleInitialHash() {
      const hash = window.location.hash.slice(1);
      if (hash) {
        const slug = hash.replace('tab-', '');
        const targetTab = this.querySelector(`[data-slug="${slug}"]`);
        if (targetTab) {
          this.hasInteracted = true;
          this.switchTab(targetTab, targetTab.dataset.tabIndex, false);
          setTimeout(() => {
            this.scrollToElement(targetTab);
          }, 100);
          return;
        }
      }

      if (this.collapsedInitial) {
        this.hideAllPanels();
        this.deactivateAllTabs();
      }
    }

    handleHashChange() {
      const hash = window.location.hash.slice(1);
      if (hash) {
        const slug = hash.replace('tab-', '');
        const targetTab = this.querySelector(`[data-slug="${slug}"]`);
        if (targetTab) {
          this.hasInteracted = true;
          this.switchTab(targetTab, targetTab.dataset.tabIndex, false);
          setTimeout(() => {
            this.scrollToElement(targetTab);
          }, 100);
        }
      }
    }

    updateURLHash(slug) {
      const newUrl = `${window.location.pathname}${window.location.search}#tab-${slug}`;
      history.pushState(null, '', newUrl);
    }

    hideAllPanels() {
      this.panels.forEach((panel) => panel.classList.add('tabs__panel--hidden'));
    }

    deactivateAllTabs() {
      this.tabs.forEach((tab) => {
        tab.setAttribute('aria-selected', 'false');
        tab.classList.remove('product-tabs__tab--active');
      });
    }

    scrollToElement(element) {
      const elementRect = element.getBoundingClientRect();
      const absoluteTop = elementRect.top + window.pageYOffset;
      const offsetPosition = absoluteTop - 150;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }

    switchTab(targetTab, tabIndex, updateHash = true) {
      this.hasInteracted = true;

      if (updateHash) {
        const slug = targetTab.dataset.slug;
        if (slug) {
          this.updateURLHash(slug);
        }
      }

      this.tabs.forEach((tab) => {
        const isSelected = tab === targetTab;
        tab.setAttribute('aria-selected', isSelected);
        if (isSelected) {
          tab.classList.add('product-tabs__tab--active');
          tab.classList.remove('button--secondary');
          tab.classList.add('button--primary');
        } else {
          tab.classList.remove('product-tabs__tab--active');
          tab.classList.remove('button--primary');
          tab.classList.add('button--secondary');
        }
      });

      this.panels.forEach((panel) => {
        const panelIndex = panel.dataset.panelIndex;
        const shouldShow = panelIndex === tabIndex;
        if (shouldShow) {
          panel.classList.remove('tabs__panel--hidden');
        } else {
          panel.classList.add('tabs__panel--hidden');
        }
      });

      if (window.innerWidth < 750) {
        targetTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }

  customElements.define('tabs-component', TabsComponent);
}
